import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Textarea } from 'components/FormFields';
import {
  Icon,
  ModalOverlay,
  PlainButton,
  SkeletonBar,
  inkA,
  orange,
  tokens,
  useIsMobile,
} from 'components/v3';
import { venueInitials } from 'hooks/useAccountVenues';
import storage from 'shared/storage';
import { useAddFormNote, useGetFormNotes } from '../gql';
import { formatDate, formatDateTime } from '../shared/dates';
import { FormStatusPill } from './FormStatus';

// Team notes on a form (design: listview.jsx NotesDrawer, the "Notes
// modal"): a centred two-pane modal with the form's summary on the left
// and the thread + composer on the right; full screen on phones, where the
// summary shrinks to a header. Notes are oldest first; ⌘/Ctrl+Enter posts.
// The design's edit / delete per note aren't here: the API has no update or
// delete note mutations yet.

const TITLE_ID = 'form-notes-title';

// The design's --sx-* surfaces in v3 terms, per theme.
export const palette = (theme) => {
  const t = tokens(theme);
  const dark = theme && theme.name === 'dark';
  return {
    ...t,
    band: dark ? t.head : t.inset,
    line2: t.fieldBorder,
    tint: orange(dark ? 0.13 : 0.1),
    tintBorder: orange(dark ? 0.28 : 0.22),
    otext: dark ? t.primary : '#d63a0f',
    field: dark ? inkA(theme, 0.04) : t.card,
    backdrop: dark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
  };
};

// The signed-in admin, as the login stored it ("Posting as …").
export const currentUser = () => {
  const user = storage.get('user');
  if (!user || typeof user !== 'object') return null;
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return { id: user.id, name: name || user.email || null };
};

const CloseButton = styled(PlainButton)`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: ${(p) => p.$color};
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    color: ${orange(1)};
    background: ${(p) => p.$hover};
  }
`;

const Eyebrow = ({ c }) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: c.primary,
      whiteSpace: 'nowrap',
    }}
  >
    <Icon name="message-square" size={13} color="currentColor" />
    Form notes
  </div>
);

Eyebrow.propTypes = { c: PropTypes.object.isRequired };

const MetaRow = ({ c, icon, label, value, first }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '14px 16px',
      borderTop: first ? 'none' : `1px solid ${c.line}`,
    }}
  >
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        width: 38,
        height: 38,
        borderRadius: 10,
        boxSizing: 'border-box',
        background: c.tint,
        border: `1px solid ${c.tintBorder}`,
        color: c.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name={icon} size={16} color="currentColor" />
    </span>
    <span
      style={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <span
        style={{
          whiteSpace: 'nowrap',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.3px',
          textTransform: 'uppercase',
          color: c.faint,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15.5,
          fontWeight: 700,
          color: c.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </span>
  </div>
);

MetaRow.propTypes = {
  c: PropTypes.object.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  first: PropTypes.bool,
};

MetaRow.defaultProps = { first: false };

// The left pane: the form's name, status and (wider screens) its facts.
const Summary = ({ form, c, mobile }) => {
  const rows = [
    {
      icon: 'flag',
      label: 'Account',
      value: form.account ? form.account.name : 'Unassigned',
    },
    {
      icon: 'inbox',
      label: 'Submissions',
      value: Number(form.submission_count || 0).toLocaleString(),
    },
    {
      icon: 'calendar-plus',
      label: 'Created',
      value: formatDate(form.created_at),
    },
    { icon: 'pen', label: 'Last updated', value: formatDate(form.updated_at) },
  ];
  return (
    <aside
      aria-label="Form summary"
      style={{
        minHeight: 0,
        overflowY: mobile ? 'visible' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: mobile ? 8 : 14,
        padding: mobile ? '16px 64px 14px 16px' : '28px 24px',
        boxSizing: 'border-box',
        background: c.inset,
        borderRight: mobile ? 'none' : `1px solid ${c.line}`,
        borderBottom: mobile ? `1px solid ${c.line}` : 'none',
      }}
    >
      <Eyebrow c={c} />
      <h2
        id={TITLE_ID}
        style={{
          margin: 0,
          fontSize: mobile ? 19 : 24,
          fontWeight: 800,
          letterSpacing: '-0.4px',
          lineHeight: 1.2,
          color: c.text,
          overflowWrap: 'anywhere',
          textWrap: 'pretty',
        }}
      >
        {form.name}
      </h2>
      {form.status ? <FormStatusPill status={form.status} /> : null}
      {mobile ? null : (
        <div
          style={{
            alignSelf: 'stretch',
            marginTop: 8,
            border: `1px solid ${c.line}`,
            borderRadius: 14,
            overflow: 'hidden',
            background: c.card,
          }}
        >
          {rows.map((row, i) => (
            <MetaRow key={row.label} c={c} first={i === 0} {...row} />
          ))}
        </div>
      )}
    </aside>
  );
};

Summary.propTypes = {
  form: PropTypes.object.isRequired,
  c: PropTypes.object.isRequired,
  mobile: PropTypes.bool.isRequired,
};

const Avatar = ({ name, mine, c }) => (
  <span
    aria-hidden="true"
    style={{
      width: 38,
      height: 38,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.3px',
      background: mine ? c.primary : '#475569',
      color: '#ffffff',
    }}
  >
    {venueInitials(name)}
  </span>
);

Avatar.propTypes = {
  name: PropTypes.string,
  mine: PropTypes.bool.isRequired,
  c: PropTypes.object.isRequired,
};

Avatar.defaultProps = { name: '' };

const NoteCard = ({ note, mine, c, mobile }) => (
  <li
    style={{
      display: 'flex',
      gap: 14,
      padding: mobile ? 14 : '16px 18px',
      borderRadius: 14,
      border: `1px solid ${c.line}`,
      background: c.inset,
    }}
  >
    <Avatar name={note.author_name} mine={mine} c={c} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '2px 8px',
          minHeight: 30,
          marginBottom: 2,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: c.text,
            whiteSpace: 'nowrap',
          }}
        >
          {note.author_name || 'Someone'}
        </span>
        <span style={{ fontSize: 11, color: c.faint, whiteSpace: 'nowrap' }}>
          {formatDateTime(note.created_at)}
        </span>
      </div>
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.6,
          color: c.sub,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {note.body}
      </div>
    </div>
  </li>
);

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number.isRequired,
    body: PropTypes.string.isRequired,
    author_id: PropTypes.number,
    author_name: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  mine: PropTypes.bool.isRequired,
  c: PropTypes.object.isRequired,
  mobile: PropTypes.bool.isRequired,
};

const EmptyNotes = ({ c }) => (
  <div
    style={{
      margin: 'auto',
      maxWidth: 270,
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      color: c.faint,
      fontSize: 14,
      lineHeight: 1.5,
    }}
  >
    <Icon name="message-square" size={30} color={c.line2} />
    <div style={{ fontSize: 16, fontWeight: 600, color: c.sub }}>
      No notes yet
    </div>
    <div>
      Jot down what still needs fixing on this form so the team can get it
      right.
    </div>
  </div>
);

EmptyNotes.propTypes = { c: PropTypes.object.isRequired };

const LoadingNotes = () => (
  <div
    role="status"
    aria-label="Loading notes"
    style={{ display: 'grid', gap: 18 }}
  >
    {[0, 1].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 14 }}>
        <SkeletonBar $w="38px" $h="38px" $r="50%" />
        <div style={{ flex: 1, display: 'grid', gap: 8 }}>
          <SkeletonBar $w="40%" $h="12px" />
          <SkeletonBar $w="85%" $h="12px" />
        </div>
      </div>
    ))}
  </div>
);

const NotesDrawer = ({ form, open, onClose }) => {
  const theme = useTheme();
  const c = palette(theme);
  const mobile = useIsMobile();
  const { notes, loading } = useGetFormNotes(form && form.id, { skip: !open });
  const addNote = useAddFormNote();
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const threadRef = useRef(null);
  // The composer's <textarea> (FormFields Textarea takes no ref), set when
  // the composer mounts so the modal can focus it on open.
  const boxRef = useRef(null);
  const holdBox = (el) => {
    boxRef.current = el ? el.querySelector('textarea') : null;
  };
  const me = currentUser();

  useEffect(() => {
    if (!open) {
      setDraft('');
      setError(null);
    }
  }, [open]);

  // Keep the newest note in view.
  const count = notes.length;
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, open]);

  if (!form) return null;

  const focusBox = () => {
    if (boxRef.current) boxRef.current.focus();
  };

  const post = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    setError(null);
    const result = await addNote({ form_id: form.id, body });
    setPosting(false);
    if (result.error) {
      setError(result.error.fields.body || result.error.global);
      return;
    }
    setDraft('');
    focusBox();
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      post();
    }
  };

  let thread;
  if (loading && !count) thread = <LoadingNotes />;
  else if (!count) thread = <EmptyNotes c={c} />;
  else {
    thread = (
      <ul
        aria-label="Notes"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {notes.map((note) => (
          <NoteCard
            key={note.id}
            note={note}
            mine={!!me && me.id != null && note.author_id === me.id}
            c={c}
            mobile={mobile}
          />
        ))}
      </ul>
    );
  }

  const close = (
    <CloseButton
      aria-label="Close notes"
      data-help="dialog-close"
      onClick={onClose}
      $size={mobile ? 44 : 36}
      $color={c.faint}
      $hover={c.tint}
      style={
        mobile
          ? { position: 'absolute', top: 12, right: 12, zIndex: 1 }
          : undefined
      }
    >
      <Icon name="x" size={mobile ? 20 : 18} color="currentColor" />
    </CloseButton>
  );

  return (
    <ModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={TITLE_ID}
      placement={mobile ? 'full' : 'center'}
      backdrop={c.backdrop}
      initialFocusRef={mobile ? undefined : boxRef}
      panelStyle={{
        width: mobile ? '100%' : 'min(1040px, calc(100vw - 48px))',
        height: mobile ? '100%' : 'min(720px, calc(100vh - 48px))',
        display: 'grid',
        gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '320px minmax(0, 1fr)',
        gridTemplateRows: mobile ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        overflow: 'hidden',
        borderRadius: mobile ? 0 : 20,
        border: mobile ? 'none' : `1px solid ${c.line2}`,
        background: c.card,
        boxShadow: mobile ? 'none' : c.shadowPop,
        color: c.text,
      }}
    >
      {/* First in the DOM on phones so it takes the opening focus, not the
          composer (which would pop the keyboard up). */}
      {mobile ? close : null}
      <Summary form={form} c={c} mobile={mobile} />
      <section
        aria-label="Notes thread"
        style={{
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: mobile ? '12px 16px' : '20px 24px 16px',
            borderBottom: `1px solid ${c.line}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.2px',
              color: c.text,
            }}
          >
            Notes
            {count ? (
              <span
                aria-label={`${count} ${count === 1 ? 'note' : 'notes'}`}
                style={{
                  minWidth: 24,
                  height: 24,
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  borderRadius: 999,
                  background: c.tint,
                  color: c.otext,
                  fontSize: 12,
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {count}
              </span>
            ) : null}
          </div>
          {mobile ? null : close}
        </div>
        <div
          ref={threadRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: mobile ? '14px 16px' : '22px 24px',
          }}
        >
          {thread}
        </div>
        <div
          style={{
            flex: 'none',
            padding: mobile
              ? '12px 16px calc(14px + env(safe-area-inset-bottom))'
              : '16px 24px 20px',
            background: c.band,
            borderTop: `1px solid ${c.line}`,
          }}
        >
          <div ref={holdBox}>
            <Textarea
              aria-label="Add a note"
              placeholder="Add a note… (⌘/Ctrl + Enter to post)"
              value={draft}
              rows={3}
              error={!!error}
              helperText={error || undefined}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              style={{
                boxSizing: 'border-box',
                minHeight: 84,
                fontSize: 15,
                lineHeight: 1.5,
                background: c.field,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 10,
            }}
          >
            <span
              style={{
                minWidth: 0,
                fontSize: 11,
                color: c.faint,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {`Posting as ${(me && me.name) || 'you'}`}
            </span>
            <ButtonV2
              primary
              onClick={post}
              disabled={!draft.trim() || posting}
              icon={<Icon name="send" size={15} color="currentColor" />}
              style={{ width: 'auto', flexShrink: 0 }}
            >
              {posting ? 'Posting…' : 'Post note'}
            </ButtonV2>
          </div>
        </div>
      </section>
    </ModalOverlay>
  );
};

NotesDrawer.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['live', 'draft']),
    account: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    submission_count: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

NotesDrawer.defaultProps = {
  form: null,
};

export default NotesDrawer;
